import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2, Search, AlertCircle, CircleSlash } from "lucide-react";

/** Global Add Video — Deliverables Phase 4 Part C (2026-08). Project-less entry point (no
 * /projects/{id} in context) with its own multi-project picker — backend enforces strict
 * dedupe by the underlying YouTube/Vimeo media id (POST /v1/deliverables), so re-adding the
 * same video from here just ATTACHES it to the newly picked project(s) instead of creating a
 * second Films & Deliverables row. Surfaced from VideoLibrary.jsx's "Add Video" button. */
export const GlobalAddVideoModal = ({ onClose, onAdded }) => {
  const [projects, setProjects] = useState([]);
  const [projectQuery, setProjectQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [title, setTitle] = useState("");
  const [finalUrl, setFinalUrl] = useState("");
  const [category, setCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [portfolio, setPortfolio] = useState(false);
  const [description, setDescription] = useState("");
  const [durationSec, setDurationSec] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [dedupeInfo, setDedupeInfo] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/v1/projects/").then((r) => setProjects(r.data.items || [])).catch(() => setProjects([]));
    api.get("/v1/deliverables/category-options").then((r) => setCategoryOptions(r.data.category_options || [])).catch(() => {});
  }, []);

  const filteredProjects = useMemo(() => {
    const ql = projectQuery.toLowerCase().trim();
    return projects.filter((p) => !ql || (p.name || "").toLowerCase().includes(ql) || (p.client_name || "").toLowerCase().includes(ql));
  }, [projects, projectQuery]);

  const toggleProject = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const onUrlChange = (val) => {
    setFinalUrl(val);
    setDedupeInfo(null);
    setThumbnailUrl(null);
    if (!/youtu\.?be|vimeo\.com/i.test(val)) return;
    const url = val.trim();
    setEnriching(true);
    Promise.all([
      api.get("/v1/videos/enrich", { params: { url } }).catch(() => ({ data: { resolved: false } })),
      api.get("/v1/deliverables/check-url", { params: { url } }).catch(() => ({ data: { exists: false } })),
    ]).then(([enrich, dedupe]) => {
      const d = enrich.data;
      if (d.resolved) {
        if (!title && d.title) setTitle(d.title);
        if (!description && d.description) setDescription(d.description);
        if (d.duration_sec != null) setDurationSec(d.duration_sec);
        setThumbnailUrl(d.thumbnail_url || null);
      }
      if (dedupe.data.exists) setDedupeInfo(dedupe.data);
    }).finally(() => setEnriching(false));
  };

  const submit = async () => {
    if (!title.trim() || !finalUrl.trim()) { toast.error("Title and Final URL are required"); return; }
    if (selectedIds.length === 0 && !category) { toast.error("Pick a category, or link to at least one project so it can be inferred"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/v1/deliverables", {
        title: title.trim(), final_url: finalUrl.trim(), project_ids: selectedIds,
        category: category || undefined, portfolio, description: description.trim() || undefined,
        duration_sec: durationSec || undefined, thumbnail_url: thumbnailUrl || undefined,
      });
      toast.success(r.data.attached_existing ? "Already existed. Linked to the selected project(s)" : "Deliverable added");
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail?.code || "Couldn't add this video");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="global-add-video-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="global-add-video-backdrop" />
      <div className="relative w-full max-w-lg h-full bg-paper border-l border-hairline overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between border-b border-hairline px-5 py-4 sticky top-0 bg-paper z-10">
          <h2 className="font-editorial text-xl tracking-tight">Add Video</h2>
          <button data-testid="global-add-video-close-btn" onClick={onClose} className="text-faint hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-faint">Final URL (YouTube or Vimeo)</label>
            <input data-testid="global-add-video-url-input" value={finalUrl} onChange={(e) => onUrlChange(e.target.value)} placeholder="Paste a link to auto-fill Title, Thumbnail &amp; Duration…"
              className="mt-1 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm focus:outline-none focus:border-brand/50" />
            {enriching && <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-faint"><Loader2 className="h-3 w-3 animate-spin" /> Fetching…</div>}
          </div>

          {dedupeInfo && (
            <div data-testid="global-add-video-dedupe-warning" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              Already added as "{dedupeInfo.deliverable?.name}"{dedupeInfo.linked_projects?.length ? ` (linked to ${dedupeInfo.linked_projects.join(", ")})` : ""}. Submitting will just link it to your selected project(s) too, not create a duplicate.
            </div>
          )}

          {thumbnailUrl && <img src={thumbnailUrl} alt="" className="w-full aspect-video object-cover rounded-md border border-hairline" />}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-faint">Title</label>
            <input data-testid="global-add-video-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm focus:outline-none focus:border-brand/50" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-faint">Description</label>
            <textarea data-testid="global-add-video-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="mt-1 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm focus:outline-none focus:border-brand/50 resize-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-faint">Category</label>
            <select data-testid="global-add-video-category-select" value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm focus:outline-none focus:border-brand/50">
              <option value="">Auto (from first selected project)</option>
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-faint">
            <input type="checkbox" data-testid="global-add-video-portfolio-checkbox" checked={portfolio} onChange={(e) => setPortfolio(e.target.checked)} className="rounded border-hairline" />
            Mark as portfolio-worthy
          </label>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-faint">Link to Project(s) <span className="text-faint/70">(optional)</span></label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-faint" />
              <input data-testid="global-add-video-project-search" value={projectQuery} onChange={(e) => setProjectQuery(e.target.value)} placeholder="Search projects…"
                className="w-full rounded-md border border-hairline bg-paper pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-brand/50" />
            </div>
            <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-hairline divide-y divide-hairline">
              {filteredProjects.length === 0 ? (
                <div className="px-3 py-4 text-xs text-faint inline-flex items-center gap-1.5"><CircleSlash className="h-3.5 w-3.5" /> No projects match</div>
              ) : (
                filteredProjects.map((p) => (
                  <label key={p.id} data-testid={`global-add-video-project-row-${p.id}`} className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-surface2">
                    <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleProject(p.id)} className="rounded border-hairline" />
                    <span className="truncate">{p.name}{p.client_name ? ` · ${p.client_name}` : ""}</span>
                  </label>
                ))
              )}
            </div>
            {selectedIds.length > 0 && <div className="mt-1.5 text-[10px] text-faint">{selectedIds.length} project(s) selected</div>}
          </div>

          <button onClick={submit} disabled={submitting} data-testid="global-add-video-submit-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-ink px-3 py-2 text-xs text-white hover:bg-brand disabled:opacity-50">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save Video
          </button>
        </div>
      </div>
    </div>
  );
};
